/**
 * Wire constants for the device-to-thin-client WebSocket, including the native relay's
 * private close codes.
 *
 * KEEP THIS FILE IDENTICAL ON `mainline` AND `mainlineStow`. See ThinClientUrl.js.
 */
/**
 * Loopback port of StationBuddy's offline fallback relay.
 *
 * MUST match `OosFallbackRelay.LOOPBACK_PORT` in StationBuddyFramework -- a Java constant in a
 * different package, on a different release train, with nothing enforcing the pair. A mismatch
 * presents as close code 1006 and no other signal.
 *
 * Declared here and interpolated into RELAY_URL below so this is the ONLY place the port appears.
 * As two independent literals, correcting this one when the Java constant moved would have changed
 * nothing -- the transport dials RELAY_URL -- and the symptom of that is indistinguishable from
 * the relay simply not being there.
 */
const RELAY_PORT = 51001;

export default Object.freeze({

    RELAY_PORT,

    /**
     * Plaintext because it never leaves the device; the relay holds the TLS session to the thin
     * client. This is the value the transport actually dials.
     */
    RELAY_URL: `ws://localhost:${RELAY_PORT}`,

    /**
     * Close codes the relay sends instead of a bare 1000/1006, so this side can tell which leg
     * broke. 4001-4003 mean the loopback hop succeeded but the upstream leg to the thin client
     * did not; 4004-4005 are about the relay itself and imply nothing about connectivity.
     * The distinction decides whether falling back to the direct URL helps or hurts.
     *
     * These MUST stay in step with `OosFallbackRelay`'s constants in StationBuddyFramework.
     * Nothing enforces that across the two languages, and a code this side does not recognise
     * degrades to the generic retry-the-relay path - which is how 4004 was originally missed.
     */
    CLOSE_CODES: Object.freeze({
        /** The thin client closed the connection cleanly. */
        UPSTREAM_CLOSED: 4001,
        /** The relay could not reach the thin client -- stale pinned address, or TC down. */
        UPSTREAM_FAILED: 4002,
        /** The relay could not determine a thin-client endpoint to dial at all. */
        NO_UPSTREAM: 4003,
        /**
         * The relay's own feature flag is off. Its listening socket outlives a mid-session
         * disable, so the loopback connect still succeeds and this arrives immediately after.
         * Says nothing about the station's connectivity, so the direct URL is the right move.
         */
        RELAY_DISABLED: 4004,
        /**
         * Another inbound connection already holds the relay. Transient by nature -- the holder
         * may close -- so this retries rather than abandoning the relay for the session.
         */
        RELAY_BUSY: 4005
    }),

    /**
     * `processPath` marks control frames. The deployed Scanner routes Ping/Pong and Ack on
     * this field and routes offline ops on `eventName` alone, so op envelopes carry no
     * processPath at all.
     */
    PROCESS_PATH: Object.freeze({
        PING: "Ping",
        PONG: "Pong",
        ACK: "Ack"
    }),

    /** Values passed to an onStateChange handler. */
    STATES: Object.freeze({
        OPEN: "OPEN",
        CLOSED: "CLOSED"
    })
});
